import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTenant } from '../lib/tenantContext';

export type PaymentMethod =
  | 'cash' | 'venmo' | 'cashapp' | 'zelle' | 'apple_pay' | 'card' | 'check' | 'other';

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  venmo: 'Venmo',
  cashapp: 'CashApp',
  zelle: 'Zelle',
  apple_pay: 'Apple Pay',
  card: 'Card',
  check: 'Check',
  other: 'Other',
};

export function PaymentModal({
  open,
  defaultAmountCents,
  onConfirm,
  onClose,
}: {
  open: boolean;
  defaultAmountCents: number;
  onConfirm: (args: { amount_collected_cents: number; payment_method: PaymentMethod }) => void;
  onClose: () => void;
}) {
  const { tenant } = useTenant();
  const accepted: PaymentMethod[] =
    (tenant?.settings?.payment_methods as PaymentMethod[] | undefined)?.filter((m) =>
      Object.prototype.hasOwnProperty.call(METHOD_LABEL, m)
    ) ?? ['cash', 'card', 'venmo', 'cashapp', 'zelle', 'apple_pay', 'check', 'other'];

  const [amountStr, setAmountStr] = useState(((defaultAmountCents ?? 0) / 100).toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>(accepted[0] ?? 'cash');

  // Reset form whenever modal opens fresh
  useEffect(() => {
    if (open) {
      setAmountStr(((defaultAmountCents ?? 0) / 100).toFixed(2));
      setMethod(accepted[0] ?? 'cash');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultAmountCents]);

  function submit() {
    const dollars = parseFloat(amountStr);
    if (Number.isNaN(dollars) || dollars < 0) return;
    onConfirm({
      amount_collected_cents: Math.round(dollars * 100),
      payment_method: method,
    });
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="card w-full sm:max-w-md space-y-4"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Mark complete</h2>
              <button onClick={onClose} className="text-muted hover:text-text">
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="label">Amount collected</label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-mono text-muted">$</span>
                <input
                  className="input font-mono text-2xl"
                  inputMode="decimal"
                  type="text"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value.replace(/[^\d.]/g, ''))}
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted mt-1">Tips and discounts go here. Service price was the default.</p>
            </div>

            <div>
              <label className="label">Payment method</label>
              <div className="grid grid-cols-3 gap-2">
                {accepted.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMethod(m)}
                    className={`p-3 rounded-lg border text-sm font-semibold ${
                      method === m
                        ? 'border-accent bg-accent/10 text-accent-2'
                        : 'border-border text-muted hover:border-slate-500'
                    }`}
                  >
                    {METHOD_LABEL[m]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost">Cancel</button>
              <button onClick={submit} className="btn-primary">Complete</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
